import { EventEmitter } from 'libs/utils/event-emitter';

import { BadgeTextbox } from './elements/textbox';
import { BadgeQrCode } from './elements/qr-code';
import { BadgeImage } from './elements/image';
import { EditorElementsController } from './elements/controller';
import { EditorState, sanitizeTextboxProps, TEXTBOX_PROPS, sanitizeQrCodeProps, QR_CODE_PROPS, sanitizeImageProps, IMAGE_PROPS } from './state';
import { DefaultQrCode, DefaultTextbox, DefaultImage, ToolName } from 'shared/libs/providers/badge-editor/enum';

/**
 * BadgeEditorController
 *
 * Main controller responsible for business logic, state management, and orchestration
 * of the badge editor. This class serves as the parent controller that coordinates
 * between the state layer and the view layer (EditorElementsController).
 *
 * Responsibilities:
 * - Managing editor state (undo/redo, state updates, persistence)
 * - Coordinating data flow between JSON state and Fabric canvas
 * - Handling business logic for elements (create, update, remove, duplicate)
 * - Managing image cache for assets
 * - Orchestrating high-level operations (zoom, tools, background)
 * - Syncing state changes to the canvas view
 *
 * Architecture:
 * - State Layer: EditorState - manages state history and updates
 * - View Layer: EditorElementsController - handles canvas operations
 * - Controller Layer: BadgeEditorController (this class) - coordinates everything
 *
 * @emits element-modified, element-added, selection, zoom, backdrop, image-placeholder-removed
 */
export class BadgeEditorController extends EventEmitter {
    /**
     * Create an editor instance
     *
     * @param {HTMLCanvasElement} canvas The canvas to render to
     * @param {HTMLDivElement} wrapper Wrapper of the canvas
     * @param {BadgeEditor.State} state Initial state of the editor
     * @param {Map<string, Image> | undefined} imageCache
     */
    constructor(canvas, wrapper, state, imageCache) {
        super();
        /** @type {Function[]} */
        this.unsubscribers = [];

        /** @type {Map<string,HTMLImageElement>} key is asset _id */
        this.imageCache = imageCache ?? new Map();

        /** @type  {light|dark} */
        this.theme = window.$cookies.get('theme') || 'light';

        /** @property @private {fabric.Canvas} canvas */
        this.canvas = canvas;

        /** @type {EditorState} */
        this.state = new EditorState(state);

        /** @type {EditorElementsController} */
        this.elements = new EditorElementsController(canvas, this.emit.bind(this));

        this.setupState();
        this.setupResizeObserver(wrapper);
        this.setupImagePlaceholderRemoved();
        this.setupElementAdded();
    }

    /** @type {() => { width: number, height: number }} */
    getBackdropDimensions() {
        return this.elements.getBackdropDimensions();
    }

    /** @type {(id: string, image: HTMLImageElement) => void} */
    cacheImage(id, image) {
        this.imageCache.set(id, image);
    }

    /**
     * Update current state, create a new entry in state history and emit new state update
     *
     * @param {(state: BadgeEditor.State) => void} callback Function that is called with deep cloned state to be modified
     * @emits EditorState#state
     */
    updateState(callback) {
        this.state.update(callback);
        this.syncState();
    }

    /**
     * Replace current state (when changing page)
     * @param {BadgeEditor.State} value
     */
    replaceState(value) {
        this.state.replace(value);
        this.syncState();
    }

    /**
     * Update element by id and type
     *
     * @param {string} id
     * @param {'text'|'qrcode'|'image'} type
     * @param {Record<string,string|number>} data
     */
    updateElement(id, type, data) {
        if (!this.elements.get(id)) return;

        // reset scaling
        if (type === 'qrcode') {
            const { width, height } = this.getImageFromCache('qrcode');
            data = this.scaleProps(data, { width, height });
        }
        if (type === 'image') {
            const { width, height } = this.getImageFromCache(data.id);
            data = this.scaleProps(data, { width, height });
        }

        this.updateState(state => {
            const block = this.findBlockById(state, type, id);
            if (!block) return;
            for (const [key, value] of Object.entries(data)) {
                if (block[key] !== data[key]) {
                    block[key] = value;
                }
            }
        });
    }

    duplicateElement(id, type) {
        const _id = this.elements.generateId();

        this.updateState(state => {
            const block = this.findBlockById(state, type, id);
            if (!block) return;
            this.addToBlock(state, type, { ...block, _id, top: block.top + block.height * (block.scaleY ?? 1) });
        });
        this.elements.activate(_id);

        return _id;
    }

    /**
     * Remove element by id and type
     *
     * @param {string} id
     * @param {'text'|'qrcode'|'image'} type
     */
    removeElement(id, type) {
        this.updateState(state => {
            state.blocks[type] = (state.blocks[type] || []).filter(({ _id }) => _id !== id);
        });
    }

    /**
     * Rotate element and sync position if changed
     *
     * @param {BadgeEditor.Image|BadgeEditor.QrCode|BadgeEditor.Textbox} data
     * @param {BadgeTextbox|BadgeQrCode|BadgeImage} element
     * @param {BadgeEditor.ImageBlock|BadgeEditor.QrCodeBlock|BadgeEditor.TextBlock} elementState
     * @returns {BadgeEditor.Image|BadgeEditor.QrCode|BadgeEditor.Textbox}
     */
    rotateAndSyncElement(data, element, elementState) {
        if (data.angle !== element.angle) {
            element.rotate(data.angle);
            data.left = element.left;
            data.top = element.top;
            elementState.top = element.top;
            elementState.left = element.left;
        }

        return data;
    }

    /** @param {string} backgroundId */
    updateBackgroundImage(backgroundId) {
        this.updateState(state => {
            state.blocks.background.id = backgroundId;
        });
    }

    /** @param {{ id: string, imageId: string }} */
    updateImage({ id, imageId }) {
        this.updateState(state => {
            state.blocks.image = state.blocks.image.map(image => {
                if (image._id === id) {
                    const { width, height } = this.getImageFromCache(imageId);
                    image.id = imageId;
                    image.width = width;
                    image.height = height;
                }
                return image;
            });
        });
    }

    /**
     * Triggers JSON -> Fabric data flow, syncs Fabric to the current state
     */
    syncState() {
        // We upsert all objects from our state into Fabric
        this.syncStateTextBlocks();
        this.syncStateQrCodeBlocks();
        this.syncStateBackground();
        this.syncStateImageBlocks();

        // We remove all Fabric objects that are not in state (with exception of backdrop and background)
        this.syncStateRemove();
        this.elements.rerender();
    }

    /**
     * Sync text block data with Fabric
     */
    syncStateTextBlocks() {
        for (const text of this.state.current.blocks.text ?? []) {
            const data = sanitizeTextboxProps(text);
            let element = this.elements.get(text._id);

            if (!element) {
                element = this.elements.addTextbox(data.text, data);
            }

            element.set(this.rotateAndSyncElement(data, element, text));
            element.setCoords();
        }
    }

    /**
     * Sync background data with Fabric
     */
    syncStateBackground() {
        const background = this.state.current.blocks.background;
        const element = this.elements.getBackground();
        if (background?.id) {
            if (element && element.id !== background.id) {
                this.elements.removeBackground();
            }

            if (!element) {
                const imgElement = this.getImageFromCache(background.id);
                this.elements.addBackground(imgElement, background.id)
                    .on('mousedown', () => this.emit('selection', 'background'));
            }
        } else if (element) {
            this.elements.removeBackground();
        }
    }

    /**
     * Sync qrcode block data with Fabric
     */
    syncStateQrCodeBlocks() {
        for (const qrcode of this.state.current.blocks.qrcode ?? []) {
            const imgElement = this.getImageFromCache('qrcode');
            const dimension = imgElement.width;
            const data = this.scaleProps(sanitizeQrCodeProps(qrcode), { width: dimension, height: dimension });
            let element = this.elements.get(qrcode._id);

            if (!element) {
                element = this.elements.addQrCode(imgElement, data);
            }

            element.set(this.rotateAndSyncElement(data, element, qrcode));
            element.setCoords();
        }
    }

    /**
     * Sync image block data with Fabric
     */
    syncStateImageBlocks() {
        for (const image of this.state.current.blocks.image ?? []) {
            const imgElement = this.getImageFromCache(image.id);
            const { width, height } = imgElement;
            const data = this.scaleProps(sanitizeImageProps(image), { width, height });
            let element = this.elements.get(image._id);

            if (element) {
                if (image.id !== element.id) {
                    element.setElement(imgElement);
                    data.width = width;
                    data.height = height;
                    data.scaleX = 1;
                    data.scaleY = 1;
                }
            } else {
                element = this.elements.addImage(imgElement, this.scaleProps(data, { width, height }));
            }

            element.set(this.rotateAndSyncElement(data, element, image));
            element.setCoords();
        }
    }

    /**
     * Sync all removed elements
     */
    syncStateRemove() {
        const ids = this.state.getIds();
        for (const id of this.elements.getIds()) {
            if (!ids.includes(id)) {
                this.elements.remove(id);
            }
        }
    }

    getImageFromCache(id) {
        const imageElement = this.imageCache.get(id);
        if (!imageElement) {
            throw new Error('Image needs to be loaded and stored in cache.');
        }
        return imageElement;
    }

    /**
     * @param {import('./state').IMAGE_PROPS|import('./state').QR_CODE_PROPS} props
     * @param {{width: number, height: number}} dimensions
     */
    scaleProps(props, dimensions) {
        const { width, height } = dimensions;
        if (props.width !== width) {
            props.scaleX = props.width / width;
            props.width = width;
        }
        if (props.height !== height) {
            props.scaleY = props.height / height;
            props.height = height;
        }
        return props;
    }

    /**
     * Trigger undo and a state sync
     *
     * @emits EditorState#state
     */
    undo() {
        this.state.undo();
        this.syncState();
    }

    /**
     * Trigger redo and a state sync
     *
     * @emits EditorState#state
     */
    redo() {
        this.state.redo();
        this.syncState();
    }

    /**
     * Set the zoom to a value between maximum and minimum. The zoom focus is the center of the canvas
     *
     * @param {number} zoom
     */
    setZoom(zoom) {
        this.elements.setZoom(zoom);
    }

    /**
     * Sets up ResizeObserver to resize the canvas when its parent changes dimensions
     *
     * @param {HTMLDivElement} wrapper Element wrapping the canvas
     */
    setupResizeObserver(wrapper) {
        const recalculateDimensions = this.elements.recalculateDimensions.bind(this.elements);
        this.resizeObserver = new ResizeObserver(recalculateDimensions);
        this.resizeObserver.observe(wrapper);
        recalculateDimensions();
    }

    /**
     * Set current tool
     *
     * @param {'panning'|'textbox'|'qrcode'|'image'|'background'} tool
     */
    setTool(tool) {
        this.elements.setTool(tool);
    }

    /**
     * Sets up Fabric -> JSON data flow
     */
    setupState() {
        this.unsubscribers.push(this.addEventListener('element-modified', (event) => {
            const target = event.target;

            if (target instanceof BadgeTextbox) {
                const textbox = target.toObject(TEXTBOX_PROPS);

                this.state.update((state) => {
                    const block = this.findBlockById(state, 'text', textbox._id);

                    if (block) {
                        Object.assign(block, sanitizeTextboxProps(textbox));
                    }
                });
                return;
            }

            if (target instanceof BadgeQrCode) {
                const qrCode = target.toObject(QR_CODE_PROPS);

                this.state.update((state) => {
                    const block = this.findBlockById(state, 'qrcode', qrCode._id);

                    if (block) {
                        Object.assign(block, sanitizeQrCodeProps(qrCode));
                    }
                });
                return;
            }

            if (target instanceof BadgeImage) {
                const image = target.toObject(IMAGE_PROPS);

                this.state.update((state) => {
                    const block = this.findBlockById(state, 'image', image._id);

                    if (block) {
                        Object.assign(block, sanitizeImageProps(image));
                    }
                });
            }
        }));
    }

    /**
     * Sets up listener for when an image placeholder is removed
     */
    setupImagePlaceholderRemoved() {
        this.unsubscribers.push(
            this.addEventListener('image-placeholder-removed', (id) => this.removeElement(id, 'image'))
        );
    }

    /**
     * Sets up listener for when an element is added
     */
    setupElementAdded() {
        this.unsubscribers.push(
            this.addEventListener('element-added', ({ id, type, left, top }) => {
                this.updateState(state => {
                    switch (type) {
                        case ToolName.TEXT_BOX:
                            this.addToBlock(state, 'text', {
                                ...DefaultTextbox,
                                _id: id,
                                left,
                                top,
                            });
                            break;
                        case ToolName.QR_CODE:
                            this.addToBlock(state, 'qrcode', {
                                ...DefaultQrCode,
                                _id: id,
                                isQrCode: true,
                                left,
                                top,
                            });
                            break;
                        case ToolName.IMAGE:
                            this.addToBlock(state, 'image', {
                                ...DefaultImage,
                                _id: id,
                                isImage: true,
                                id: 'placeholder',
                                left,
                                top,
                            });
                            break;
                    }
                    this.elements.activate(id);
                });
            })
        );
    }

    /**
     * Add an item to a specific block in state
     */
    addToBlock(state, element, data) {
        if (!Array.isArray(state.blocks[element])) {
            state.blocks[element] = [];
        }
        state.blocks[element].push(data);
    }

    /**
     * Updates the dimensions of the backdrop
     *
     * @param {number} width
     * @param {number} height
     */
    updateBackdropDimensions(width, height) {
        this.elements.updateBackdropDimensions(width, height);
    }

    /**
     * Returns the current design as an SVG string
     *
     * @returns {string}
     */
    asSVG() {
        return this.elements.asSVG();
    }

    /** @type {(state: BadgeEditor.State, type: 'text'|'qrcode'|'image', id: string) => BadgeEditor.ImageBlock|BadgeEditor.QrCodeBlock|BadgeEditor.TextboxBlock|undefined} */
    findBlockById(state, type, id) {
        return state.blocks[type].find(({ _id }) => _id === id);
    }

    /**
     * Disposes the underlying Fabric object structure
     */
    dispose() {
        this.elements.dispose();
        this.unsubscribe(this.unsubscribers);
        this.resizeObserver.disconnect();
    }
}
